import type { TealiumDataLayer } from "@repo/analytics";

import { ChannelType, PaymentStatus } from "@repo/ui/types/manageBooking";
import { checkIsMobileDevice } from "@repo/ui/utils/device";

import { useAppSelector } from "@repo/store/hooks";
import {
  selectBookingBaggageCart,
  useAuthState,
  useBookingDetails,
  useBundleCode,
  useCorporatePassengers,
  useCorporatePromoCode,
  useCorporateSearch,
  useFlightState,
  useLocale,
} from "@repo/store/slices";

interface TealiumPageConfig {
  match: (strippedPath: string) => boolean;
  build: (strippedPath: string) => TealiumDataLayer;
}

enum flightType {
  NONE = 0,
  NONSTOP = 1,
  THROUGH = 2,
  DIRECT = 3,
  CONNECTING = 4,
  All = 5,
}

export function usePageDataMap(pathname: string): (() => TealiumDataLayer) | null {
  const locale = useLocale();
  const authState = useAuthState();
  const isMobileDevice = checkIsMobileDevice(navigator.userAgent);
  const promocode = useCorporatePromoCode();
  const passengers = useCorporatePassengers();
  const corporateSearch = useCorporateSearch();
  const bookingDetails = useBookingDetails();
  const inboundBundleCode = useBundleCode("inbound");
  const outboundBundleCode = useBundleCode("outbound");
  const bookingBaggageCart = useAppSelector(selectBookingBaggageCart);
  const flight = useFlightState();

  // Common fields available on every page
  const buildCommonData = (): TealiumDataLayer => {
    return {
      "dom.url": window.location.href, // Current location (url) with query params included
      "dom.pathname": window.location.origin + window.location.pathname, // Current location (url) without query params included
    };
  };

  const bookingFlowInitData = (path: string): TealiumDataLayer => {
    return {
      culture: locale, // es-CL
      device: isMobileDevice ? "mobile" : "desktop",
      flow_type: "DB", // DB - During Booking, PB - Post Booking, CK - Checkin
      page: path,
      role: authState.user?.userRole ?? "WWW Anonymous",
    };
  };

  const flightCaptureData = (): TealiumDataLayer => {
    return {
      promocode: promocode ?? "",
      passengers_total_count: passengers.adults + passengers.children + passengers.infants,
      amount: bookingDetails?.breakdown.balanceDue ?? 0,
      trip_type: corporateSearch.travelDates?.to ? "R" : "O", // R = RoundTrip, O = Oneway
      RouteMarket:
        corporateSearch.departureCountryCode === corporateSearch.destinationCountryCode
          ? `DOM${corporateSearch.departureCountryCode}`
          : "Inter",
    };
  };

  const baggageCaptureData = (): TealiumDataLayer => {
    return {
      breakdown_currency: bookingDetails?.currencyCode ?? "",
      breakdown_total_amount: bookingDetails?.breakdown.totalAmount ?? 0,
      departure_date: bookingDetails?.journeys?.[0]?.designator.departure ?? "",
      destination_iata: bookingDetails?.journeys?.[0]?.designator.destination ?? "",
      inbound_bundle: inboundBundleCode,
      inbound_departure_date: bookingDetails?.journeys?.[1]?.designator.departure ?? "",
      origin_iata: bookingDetails?.journeys?.[0]?.designator.origin ?? "",
      outbound_departure_date: bookingDetails?.journeys?.[0]?.designator.departure ?? "",
      outbound_bundle: outboundBundleCode,
    };
  };

  const getFareClass = (direction: string): string => {
    const journey =
      direction === "inbound" ? bookingDetails?.journeys[1] : bookingDetails?.journeys[0];

    if (
      journey?.flightType === flightType.CONNECTING &&
      journey?.segments?.[0]?.fares?.[0]?.classOfService &&
      journey?.segments?.[1]?.fares?.[0]?.classOfService
    ) {
      return `${journey.segments[0].fares[0].classOfService}|${journey.segments[1].fares[0].classOfService}`;
    }

    return journey?.segments?.[0]?.fares?.[0]?.classOfService ?? "";
  };

  const seatsCaptureData = (): TealiumDataLayer => {
    return {
      outbound_fare_amount: flight.outbound.memberFare ?? 0,
      inbound_fare_amount: flight.inbound.memberFare ?? 0,
      outbound_fare_class: getFareClass("outbound"),
      inbound_fare_class: getFareClass("inbound"),
      outbound_is_connected_flight:
        bookingDetails?.journeys?.[0]?.flightType === flightType.CONNECTING,
      inbound_is_connected_flight:
        bookingDetails?.journeys?.[1]?.flightType === flightType.CONNECTING,
    };
  };

  const extrasCaptureData = (): TealiumDataLayer => {
    return {
      seats_count:
        bookingDetails?.breakdown?.passengerTotals?.seats?.charges.filter(
          (charge) => charge.code === "STF",
        ).length ?? 0,
    };
  };

  const itineraryCaptureData = (): TealiumDataLayer => {
    return {
      is_gds_booking: bookingDetails?.info.channelType === ChannelType.Gds,
      order_currency: bookingDetails?.currencyCode ?? "",
      PNR: bookingDetails?.recordLocator ?? "",
      successful_transaction_made: bookingDetails?.info.paidStatus === PaymentStatus.PaidInFull,
      Queues: bookingDetails?.queues
        ? bookingDetails.queues.map(({ code }) => code).join(" | ")
        : "",
    };
  };

  const countBagsBySsrCode = (ssrCode: string): number =>
    Object.values(bookingBaggageCart).reduce(
      (total, journey) =>
        total +
        (journey?.passengers ?? []).reduce(
          (passengerTotal, passenger) =>
            passengerTotal +
            (passenger.bags ?? [])
              .filter((bag) => bag.ssrCode === ssrCode)
              .reduce((bagTotal, bag) => bagTotal + bag.quantity, 0),
          0,
        ),
      0,
    );

  const oversizeBaggageCount = (): TealiumDataLayer => ({
    oversize_baggage: countBagsBySsrCode("SPQD"),
  });

  const cabinBaggageCount = (): TealiumDataLayer => ({
    cabin_bag_count: countBagsBySsrCode("LBGD"),
  });

  const checkedBaggageCount = (): TealiumDataLayer => {
    const total = countBagsBySsrCode("BAGD");

    return {
      checked_bag_count: total,
    };
  };

  const PAGE_CONFIG: TealiumPageConfig[] = [
    {
      match: (path) => path === "/",
      build: (path) => ({
        ...buildCommonData(),
        ...bookingFlowInitData(path),
      }),
    },
    {
      match: (path) => path.split("/").at(-1) === "flight",
      build: (path) => ({
        ...buildCommonData(),
        ...bookingFlowInitData(path),
        ...flightCaptureData(),
      }),
    },
    {
      match: (path) => path.split("/").at(-1) === "baggage",
      build: (path) => ({
        ...buildCommonData(),
        ...bookingFlowInitData(path),
        ...flightCaptureData(),
        ...baggageCaptureData(),
      }),
    },
    {
      match: (path) => path.split("/").at(-1) === "seats",
      build: (path) => ({
        ...buildCommonData(),
        ...bookingFlowInitData(path),
        ...flightCaptureData(),
        ...baggageCaptureData(),
        ...oversizeBaggageCount(),
        ...cabinBaggageCount(),
        ...checkedBaggageCount(),
        ...seatsCaptureData(),
      }),
    },
    {
      match: (path) => path.split("/").at(-1) === "extras",
      build: (path) => ({
        ...buildCommonData(),
        ...bookingFlowInitData(path),
        ...flightCaptureData(),
        ...baggageCaptureData(),
        ...oversizeBaggageCount(),
        ...cabinBaggageCount(),
        ...checkedBaggageCount(),
        ...seatsCaptureData(),
        ...extrasCaptureData(),
      }),
    },
    {
      match: (path) => path.split("/").at(-1) === "passengers",
      build: (path) => ({
        ...buildCommonData(),
        ...bookingFlowInitData(path),
        ...flightCaptureData(),
        ...baggageCaptureData(),
        ...oversizeBaggageCount(),
        ...cabinBaggageCount(),
        ...checkedBaggageCount(),
        ...seatsCaptureData(),
        ...extrasCaptureData(),
      }),
    },
    {
      match: (path) => path.split("/").at(-1) === "payment",
      build: (path) => ({
        ...buildCommonData(),
        ...bookingFlowInitData(path),
        ...flightCaptureData(),
        ...baggageCaptureData(),
        ...oversizeBaggageCount(),
        ...cabinBaggageCount(),
        ...checkedBaggageCount(),
        ...seatsCaptureData(),
        ...extrasCaptureData(),
      }),
    },
    {
      match: (path) => path.split("/").at(-1) === "itinerary",
      build: (path) => ({
        ...buildCommonData(),
        ...bookingFlowInitData(path),
        ...flightCaptureData(),
        ...baggageCaptureData(),
        ...oversizeBaggageCount(),
        ...cabinBaggageCount(),
        ...checkedBaggageCount(),
        ...seatsCaptureData(),
        ...extrasCaptureData(),
        ...itineraryCaptureData(),
      }),
    },
  ];

  const matched = PAGE_CONFIG.find((config) => config.match(pathname));
  return matched ? () => matched.build(pathname) : null;
}
